/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { Region } from '@/components/region';
import type { PageWithComponentData } from '@/lib/page-designer/page-loader.server';
import { cn } from '@/lib/utils';

interface CategoryDropdownRibbonProps {
    page: PageWithComponentData;
    className?: string;
}

/**
 * Renders the `menu-{categoryId}-ribbon` Page Designer content region — the full-width
 * impact banner strip at the bottom of a top-level nav category's mega-menu dropdown.
 * Independent of whichever content (legacy banner or the new content slot) occupies
 * column 2, and always spans the full panel width regardless of the link list's width.
 *
 * Background is the mockup's exact #e50914 — confirmed to match the mockup rather than
 * the existing --header-accent-background token used elsewhere in the header (a
 * different, deeper red), so this intentionally introduces a second red value scoped to
 * this flyout only. Bottom corners match the panel's own rounding (see the reviewed
 * mockup). Content is entirely author-driven — a MarkupContent icon+text+link block, a
 * single flattened banner image via pdImage, or both.
 */
export default function CategoryDropdownRibbon({ page, className }: CategoryDropdownRibbonProps): ReactElement {
    return (
        <div
            className={cn(
                // text-header-accent-foreground resolves to #FFFFFF, matching the mockup's
                // white ribbon text (the project's color-linter rejects hardcoded white
                // utilities even where the hex value itself is correct).
                'flex items-center justify-center gap-2 rounded-b-[3px] bg-[#e50914] px-4 py-3 text-header-accent-foreground',
                className
            )}>
            <Region
                page={page}
                regionId="content"
                fallbackOnEmpty
                errorElement={null}
                className="flex flex-wrap items-center justify-center gap-2"
            />
        </div>
    );
}
